/**
 * @changelog
 *
 * 2026-02-27 (0.1.0) (jhoward)
 *   - Add authRoutes array for login, enroll, verify email, and 2FA enroll routes.
 *
 * 2026-03-02 (0.1.1) (jhoward)
 *   - Change 2FA enrollment route to use WebAuthnEnroll.vue instead of removed 2FAEnroll.vue.
 *
 * 2026-03-11 (0.2.0) (jhoward)
 *   - Add new routes: '/logout', '/login/help/username', '/login/help/password', '/login/help/password/reset/:token', '/login/two-factor/auth'.
 *   - Add meta fields 'pageTitle' and 'twoFactorAuthRequired' to all routes.
 *   - Update 2FA enrollment route to use 'TwoFactorEnroll.vue'.
 *   - BREAKING: Route names and paths changed; consumers using old paths/names must update.
 *
 * 2026-03-17 (0.3.0) (jhoward)
 *   - Replace /email/verify routes with /verify/:method for multi-method verification.
 *
 * 2026-04-09 (0.4.0) (jhoward)
 *   - Add /login/two-factor/enroll and /reauthenticate routes.
 *   - Shorten 2FA auth page title.
 *
 * 2026-10-09 (0.5.0) (jhoward)
 *   - Add /login/help/two-factor and /login/help/two-factor/confirm/:token, the self-service two-factor reset.
 *   - Add /account/2fa/recovery, the recovery codes page.
 *
 * 2026-10-10 (0.6.0) (jhoward)
 *   - /account/2fa/enroll redirects to account.security; the page it served (TwoFactorEnroll.vue) is gone, its
 *     content a section of Sign-in & Security.
 */

import type { RouteRecordRaw } from 'vue-router';
import Login from '@/views/Auth/Login.vue';
import LoginTwoFactorAuth from '@/views/Auth/LoginTwoFactorAuth.vue';
import Logout from '@/views/Auth/Logout.vue';

const authRoutes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: Login,
    meta: {
      pageTitle: 'Login',
      authRequired: false,
      twoFactorAuthRequired: false,
    },
  },
  {
    path: '/logout',
    name: 'logout',
    component: Logout,
    meta: {
      pageTitle: 'Logout',
      authRequired: false,
      twoFactorAuthRequired: false,
    },
  },
  {
    path: '/login/help/username',
    name: 'login.help.username',
    component: () => import('@/views/Auth/LoginHelpUsername.vue'),
    meta: {
      pageTitle: 'Username Help',
      authRequired: false,
      twoFactorAuthRequired: false,
    },
  },
  {
    path: '/login/help/password',
    name: 'password.request',
    component: () => import('@/views/Auth/LoginHelpPassword.vue'),
    meta: {
      pageTitle: 'Password Reset',
      authRequired: false,
      twoFactorAuthRequired: false,
    },
  },
  {
    path: '/login/help/password/reset/:token',
    name: 'password.reset',
    component: () => import('@/views/Auth/LoginHelpPasswordReset.vue'),
    meta: {
      pageTitle: 'Reset Password',
      authRequired: false,
      twoFactorAuthRequired: false,
    },
    props: true,
  },
  {
    path: '/login/help/two-factor',
    name: 'login.help.two-factor',
    component: () => import('@/views/Auth/LoginHelpTwoFactor.vue'),
    meta: {
      pageTitle: 'Reset Two-Factor',
      authRequired: false,
      twoFactorAuthRequired: false,
    },
  },
  {
    path: '/login/help/two-factor/confirm/:token',
    name: 'login.help.two-factor.confirm',
    component: () => import('@/views/Auth/LoginHelpTwoFactorConfirm.vue'),
    meta: {
      pageTitle: 'Reset Two-Factor',
      authRequired: false,
      twoFactorAuthRequired: false,
    },
    props: true,
  },
  {
    path: '/login/two-factor/enroll',
    name: 'login.two-factor.enroll',
    component: () => import('@/views/Auth/LoginTwoFactorEnroll.vue'),
    meta: {
      pageTitle: 'Two-Factor Enroll',
      authRequired: true,
      twoFactorAuthRequired: false,
    },
  },
  {
    path: '/login/two-factor/auth',
    name: 'login.two-factor.auth',
    component: LoginTwoFactorAuth,
    meta: {
      pageTitle: 'Two-Factor Auth',
      authRequired: true,
      twoFactorAuthRequired: false,
    },
  },
  {
    path: '/enroll',
    name: 'enroll',
    component: () => import('../views/Auth/Enroll.vue'),
    meta: {
      pageTitle: 'Enroll',
      authRequired: false,
      twoFactorAuthRequired: false,
    },
  },
  {
    path: '/verify/:method',
    name: 'verification.notice',
    component: () => import('../views/Auth/VerifyMethod.vue'),
    meta: {
      pageTitle: 'Verify Account',
      authRequired: true,
      twoFactorAuthRequired: false,
    },
    props: true,
  },
  // {
  //   path: '/verify/:method/:id/:hash',
  //   name: 'email.verify-link',
  //   component: () => import('../views/Auth/VerifyMethod.vue'),
  //   meta: {
  //     pageTitle: 'Verify Email',
  //     authRequired: true,
  //     twoFactorAuthRequired: false,
  //   },
  //   props: true
  // },
  //Kept as a redirect: the enrollment page is now a section of Sign-in & Security, and the old link may be bookmarked.
  {
    path: '/account/2fa/enroll',
    name: 'account.2fa.enroll',
    redirect: { name: 'account.security' },
  },
  {
    path: '/account/2fa/recovery',
    name: 'account.2fa.recovery',
    component: () => import('@/views/Account/RecoveryCodes.vue'),
    meta: {
      pageTitle: 'Recovery Codes',
      authRequired: true,
      twoFactorAuthRequired: true,
    },
  },

  {
    path: '/reauthenticate',
    name: 'reauthenticate.notice',
    component: () => import('../views/Auth/Reauthenticate.vue'),
    meta: {
      pageTitle: 'Reauthentication',
      authRequired: true,
      twoFactorAuthRequired: true,
    },
  },
];

export default authRoutes;
